import { useState, useCallback } from 'react';
import {
  TutorSessionDTO,
  startSession,
  endSession,
  updateSessionPage,
  getActiveSession,
} from '../services/tutorSessionService';

interface UseTutorSessionState {
  session: TutorSessionDTO | null;
  loading: boolean;
  error: string | null;
}

export function useTutorSession(courseId: number) {
  const [state, setState] = useState<UseTutorSessionState>({
    session: null,
    loading: false,
    error: null,
  });

  const setLoading = (loading: boolean) =>
    setState((s) => ({ ...s, loading, error: null }));

  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }));

  const loadActiveSession = useCallback(async () => {
    setLoading(true);
    try {
      const session = await getActiveSession(courseId);
      setState({ session, loading: false, error: null });
    } catch {
      setState({ session: null, loading: false, error: null });
    }
  }, [courseId]);

  const handleStartSession = useCallback(async () => {
    setLoading(true);
    try {
      const session = await startSession(courseId);
      setState({ session, loading: false, error: null });
      return session;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to start session';
      setError(msg);
      throw err;
    }
  }, [courseId]);

  const handleEndSession = useCallback(async () => {
    setLoading(true);
    try {
      const session = await endSession(courseId);
      setState({ session, loading: false, error: null });
      return session;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to end session';
      setError(msg);
      throw err;
    }
  }, [courseId]);

  const handleUpdatePage = useCallback(
    async (topicId: number | null, pageIndex: number) => {
      try {
        const session = await updateSessionPage(courseId, topicId, pageIndex);
        setState((s) => ({ ...s, session }));
        return session;
      } catch (err: any) {
        const msg = err?.response?.data?.message || err.message || 'Failed to update page';
        setError(msg);
        throw err;
      }
    },
    [courseId]
  );

  return {
    session: state.session,
    loading: state.loading,
    error: state.error,
    loadActiveSession,
    startSession: handleStartSession,
    endSession: handleEndSession,
    updateCurrentPage: handleUpdatePage,
  };
}
